import addIcon from '@iconify/icons-carbon/add';
import subtractIcon from '@iconify/icons-carbon/subtract';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@mui/material';
import { memo } from 'react';

import { Iconify } from '..';

import type { AccordionProps } from '@mui/material';
import type { FC, ReactNode, SyntheticEvent } from 'react';

export interface AccordionsProps<
  T extends Omit<AccordionProps, 'children'> & {
    id: string;
    summary: string;
    details?: ReactNode;
  } = Omit<AccordionProps, 'children'> & {
    id: string;
    summary: string;
    details?: ReactNode;
  }
> {
  accordions: T[];
  accordion?: (props: T) => ReturnType<typeof Accordion>;
}

export const Accordions: FC<AccordionsProps> = memo(
  ({
    accordions,
    accordion: RenderAccordion = StandardAccordion,
    ...props
  }) => (
    <>
      {accordions.map((accordion) => (
        <RenderAccordion key={accordion.id} {...props} {...accordion} />
      ))}
    </>
  )
);

Accordions.displayName = 'Accordions';

export interface StandardAccordionProps
  extends Omit<AccordionProps, 'children'> {
  summary?: string;

  details?: ReactNode;
}

export const StandardAccordion: FC<StandardAccordionProps> = ({
  summary,
  expanded,
  details,
  ...props
}) => (
  <Accordion {...props} expanded={expanded}>
    <AccordionSummary>
      <Typography variant="h5" sx={{ flexGrow: 1 }}>
        {summary}
      </Typography>
      <Iconify
        icon={expanded ? subtractIcon : addIcon}
        sx={{ width: 24, height: 24 }}
      />
    </AccordionSummary>
    <AccordionDetails>{details}</AccordionDetails>
  </Accordion>
);

export interface SelectableAccordion extends Omit<AccordionProps, 'children'> {
  onChange: (event: SyntheticEvent, expanded: boolean) => void;

  expanded: Required<AccordionProps['expanded']>;
}

export const SelectableAccordion: FC<SelectableAccordion> = (props) => (
  <StandardAccordion {...props} />
);
